class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target?.type === 'range') return;
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput =
          event.target.getAttribute('type') === 'text' || event.target.getAttribute('type') === 'range';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.reinitAccordions();
  }

  static reinitAccordions() {
    document.querySelectorAll('details-accordion.js-filter').forEach((accordion) => {
      if (typeof accordion.reinit === 'function') accordion.reinit();
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const resolvedForm =
      form instanceof HTMLFormElement
        ? form
        : this.querySelector('form') ||
          document.getElementById('FacetFiltersForm') ||
          document.getElementById('FacetFiltersFormMobile') ||
          document.getElementById('FacetSortForm') ||
          document.getElementById('FacetSortDrawerForm');

    if (!(resolvedForm instanceof HTMLFormElement)) return '';

    const formData = new FormData(resolvedForm);
    const searchParams = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value === '') continue;
      searchParams.append(key, value);
    }

    FacetFiltersForm.stripInactivePriceParams(resolvedForm, searchParams);

    return searchParams.toString();
  }

  static stripInactivePriceParams(form, searchParams) {
    form.querySelectorAll('price-range').forEach((priceRange) => {
      if (priceRange.userModified) return;

      const slider = priceRange.querySelector('.facets__price-slider');
      if (!slider) return;

      const minParam = slider.dataset.minParam;
      const maxParam = slider.dataset.maxParam;

      if (minParam) searchParams.delete(minParam);
      if (maxParam) searchParams.delete(maxParam);
    });
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const eventTarget = event.target instanceof Element ? event.target : null;
    const targetForm =
      eventTarget?.closest('form') ||
      (event.currentTarget instanceof HTMLFormElement ? event.currentTarget : null) ||
      this.querySelector('form');

    if (!targetForm) return;

    if (eventTarget?.classList?.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(targetForm);
      if (!searchParams) return;
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = targetForm.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const params = this.createSearchParams(form);
            forms.push(params);
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          const params = this.createSearchParams(form);
          forms.push(params);
        }
      });

      if (!forms.length) return;
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.onRangeInput = this.onRangeInput.bind(this);
    this.onRangeChange = this.onRangeChange.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('.facets__price-slider');
    if (!this.slider) return;

    this.rangeMax = Number(this.slider.dataset.rangeMax) || 0;
    this.step = Number(this.slider.dataset.step) || 1;
    this.userModified = this.slider.dataset.priceActive === 'true';
    this.minInput = this.slider.querySelector('.facets__price-input--min');
    this.maxInput = this.slider.querySelector('.facets__price-input--max');
    this.minRange = this.slider.querySelector('.facets__price-range--min');
    this.maxRange = this.slider.querySelector('.facets__price-range--max');
    this.progress = this.slider.querySelector('.facets__price-slider-progress');
    this.fromField = this.slider.querySelector('.facets__price-field--from');
    this.toField = this.slider.querySelector('.facets__price-field--to');

    this.minRange?.addEventListener('input', this.onRangeInput);
    this.maxRange?.addEventListener('input', this.onRangeInput);
    this.minRange?.addEventListener('change', this.onRangeChange);
    this.maxRange?.addEventListener('change', this.onRangeChange);
    this.minRange?.addEventListener('pointerdown', () => this.setActiveThumb('min'));
    this.maxRange?.addEventListener('pointerdown', () => this.setActiveThumb('max'));

    [this.minInput, this.maxInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', this.onInputChange);
      input.addEventListener('change', this.onInputChange);
      input.addEventListener('focus', this.onInputFocus);
      input.addEventListener('blur', this.onInputBlur);
      input.addEventListener('keydown', this.onKeyDown);
    });

    this.syncRangeAttributes();
    this.syncSlidersFromInputs();
    this.updateUI();
  }

  syncRangeAttributes() {
    [this.minRange, this.maxRange].forEach((range) => {
      if (!range) return;
      range.min = '0';
      range.max = String(this.rangeMax);
      range.step = String(this.step);
    });

    if (!this.userModified && this.maxRange) {
      this.maxRange.value = String(this.rangeMax);
    }
  }

  disconnectedCallback() {
    this.minRange?.removeEventListener('input', this.onRangeInput);
    this.maxRange?.removeEventListener('input', this.onRangeInput);
    this.minRange?.removeEventListener('change', this.onRangeChange);
    this.maxRange?.removeEventListener('change', this.onRangeChange);
    [this.minInput, this.maxInput].forEach((input) => {
      if (!input) return;
      input.removeEventListener('input', this.onInputChange);
      input.removeEventListener('change', this.onInputChange);
      input.removeEventListener('focus', this.onInputFocus);
      input.removeEventListener('blur', this.onInputBlur);
      input.removeEventListener('keydown', this.onKeyDown);
    });
  }

  onInputFocus(event) {
    const input = event.currentTarget;
    if (input === this.minInput) {
      this.fromField?.classList.add('facets__price-field--focused');
      this.setActiveThumb('min');
    } else {
      this.toField?.classList.add('facets__price-field--focused');
      this.setActiveThumb('max');
    }
  }

  onInputBlur(event) {
    const input = event.currentTarget;
    if (input === this.minInput) {
      this.fromField?.classList.remove('facets__price-field--focused');
    } else {
      this.toField?.classList.remove('facets__price-field--focused');
    }
    this.syncSlidersFromInputs();
    this.updateUI();
  }

  onInputChange() {
    this.userModified = true;
    this.syncSlidersFromInputs();
    this.updateUI();
  }

  setActiveThumb(thumb) {
    this.slider.classList.toggle('facets__price-slider--min-active', thumb === 'min');
    this.slider.classList.toggle('facets__price-slider--max-active', thumb === 'max');
  }

  onRangeInput(event) {
    this.clampRanges(event.currentTarget);
    this.syncInputsFromSliders();
    this.updateUI();

    if (event.currentTarget === this.minRange) {
      this.fromField?.classList.add('facets__price-field--focused');
      this.toField?.classList.remove('facets__price-field--focused');
    } else {
      this.toField?.classList.add('facets__price-field--focused');
      this.fromField?.classList.remove('facets__price-field--focused');
    }
  }

  onRangeChange() {
    this.userModified = true;
    this.syncInputsFromSliders();
    this.updateUI();
    this.submitFilters(this.maxInput);
    this.fromField?.classList.remove('facets__price-field--focused');
    this.toField?.classList.remove('facets__price-field--focused');
    this.slider.classList.remove('facets__price-slider--min-active', 'facets__price-slider--max-active');
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  clampRanges(activeRange) {
    let minVal = Number(this.minRange.value);
    let maxVal = Number(this.maxRange.value);
    const minGap = this.step;

    if (minVal > maxVal - minGap) {
      if (activeRange === this.minRange) {
        minVal = Math.max(0, maxVal - minGap);
        this.minRange.value = minVal;
      } else {
        maxVal = Math.min(this.rangeMax, minVal + minGap);
        this.maxRange.value = maxVal;
      }
    }
  }

  syncInputsFromSliders() {
    const minAmount = Number(this.minRange.value);
    const maxAmount = Number(this.maxRange.value);

    if (this.minInput) {
      this.minInput.value = minAmount <= 0 ? '' : this.formatInputValue(minAmount);
    }
    if (this.maxInput) {
      this.maxInput.value = this.isAtRangeMax(maxAmount) ? '' : this.formatInputValue(maxAmount);
    }
    this.setMinAndMaxValues();
    this.updateInputNames();
  }

  updateInputNames() {
    if (!this.slider || !this.minInput || !this.maxInput) return;

    const minParam = this.slider.dataset.minParam;
    const maxParam = this.slider.dataset.maxParam;
    if (!minParam || !maxParam) return;

    if (!this.userModified) {
      this.minInput.removeAttribute('name');
      this.maxInput.removeAttribute('name');
      return;
    }

    const minValue = this.parseInputValue(this.minInput.value);
    const maxValue =
      this.maxInput.value === '' || this.maxInput.value == null
        ? this.rangeMax
        : this.parseInputValue(this.maxInput.value);

    if (minValue <= 0) {
      this.minInput.removeAttribute('name');
    } else {
      this.minInput.setAttribute('name', minParam);
    }

    if (this.maxInput.value === '' || this.isAtRangeMax(maxValue)) {
      this.maxInput.removeAttribute('name');
    } else {
      this.maxInput.setAttribute('name', maxParam);
    }
  }

  syncSlidersFromInputs() {
    const minAmount = this.parseInputValue(this.minInput?.value || '0');
    let maxAmount =
      this.maxInput?.value === '' || this.maxInput?.value == null
        ? this.rangeMax
        : this.parseInputValue(this.maxInput.value);

    maxAmount = Math.min(Math.max(maxAmount, minAmount), this.rangeMax);
    const minClamped = Math.min(minAmount, maxAmount);

    this.minRange.value = minClamped;
    this.maxRange.value = maxAmount;
    this.clampRanges(this.maxRange);
    this.syncInputsFromSliders();
  }

  updateUI() {
    const minAmount = Number(this.minRange.value);
    const maxAmount = Number(this.maxRange.value);
    const range = this.rangeMax || 1;

    if (this.progress) {
      const left = (minAmount / range) * 100;
      const width = ((maxAmount - minAmount) / range) * 100;
      this.progress.style.left = `${left}%`;
      this.progress.style.width = `${Math.max(width, 0)}%`;
    }
  }

  submitFilters(targetInput) {
    const form = this.closest('form');
    const facetFiltersForm = this.closest('facet-filters-form');
    if (!form || !facetFiltersForm) return;

    const event = {
      preventDefault() {},
      target: targetInput || this.maxInput || this.minInput || form,
      currentTarget: form,
    };

    if (typeof facetFiltersForm.onSubmitHandler === 'function') {
      facetFiltersForm.onSubmitHandler(event);
    } else {
      form.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  setMinAndMaxValues() {
    if (!this.minInput || !this.maxInput) return;

    if (this.maxInput.value) this.minInput.setAttribute('data-max', this.maxInput.value);
    if (this.minInput.value) this.maxInput.setAttribute('data-min', this.minInput.value);
    if (this.minInput.value === '') this.maxInput.setAttribute('data-min', '0');
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('data-max', this.maxInput.getAttribute('data-max') || String(this.rangeMax));
    }
  }

  isAtRangeMax(amount) {
    return amount >= this.rangeMax - this.step / 2;
  }

  parseInputValue(value) {
    if (!value) return 0;
    const amount = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(amount) ? amount : 0;
  }

  formatInputValue(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(2);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
